import { Tooltip } from "antd";
import React from "react";
import styles from "../styles.module.scss";
import {
  CampaignTimelineStageId,
  DragMode,
  DragState,
  PlacedStage,
} from "../types";

interface GanttTrackProps {
  trackKey: string;
  creatorId: string | number;
  deliverableId: string;
  deliverableName: string;
  stages: PlacedStage[];
  ticks: Date[];
  todayPct: number;
  editMode: boolean;
  rowInSelection: boolean;
  drag: DragState | null;
  registerTrackRef: (key: string, el: HTMLDivElement | null) => void;
  beginDrag: (
    e: React.PointerEvent,
    mode: DragMode,
    row: {
      creatorId: string | number;
      deliverableName: string;
      deliverableId: string;
    },
    stageId: CampaignTimelineStageId
  ) => void;
  onOpenDeliverable?: (
    creatorId: string,
    deliverableId?: string,
    taskId?: string
  ) => void;
}

const BAR_COLOR_CLASSES: Record<string, string> = {
  done: styles.stageDone,
  active: styles.stageActive,
  blocked: styles.stageBlocked,
  upcoming: styles.stageUpcoming,
  cancelled: styles.stageCancelled,
};

const LABEL_COLOR_CLASSES: Record<string, string> = {
  done: styles.labelDone,
  active: styles.labelActive,
  blocked: styles.labelBlocked,
  upcoming: styles.labelUpcoming,
  cancelled: styles.labelCancelled,
};

export const GanttTrack: React.FC<GanttTrackProps> = ({
  trackKey,
  creatorId,
  deliverableId,
  deliverableName,
  stages,
  ticks,
  todayPct,
  editMode,
  rowInSelection,
  drag,
  registerTrackRef,
  beginDrag,
  onOpenDeliverable,
}) => {
  const rowContext = { creatorId, deliverableName, deliverableId };

  return (
    <div className={styles.trackContainer}>
      {/* Background grid lines for ticks */}
      <div className={styles.gridLinesContainer}>
        {ticks.map((_, i) => (
          <div
            key={i}
            className={styles.gridLine}
            style={{
              left: `${(i / Math.max(ticks.length - 1, 1)) * 100}%`,
            }}
          />
        ))}
        {todayPct >= 0 && todayPct <= 100 && (
          <div
            className={styles.todayLine}
            style={{ left: `${todayPct}%` }}
            title="Today"
          />
        )}
      </div>

      {/* Main Track Element */}
      <div
        ref={(el) => registerTrackRef(trackKey, el)}
        className={`${styles.trackBar} ${editMode ? styles.trackBarEditMode : ""}`}
      >
        {stages.length === 0 ? (
          <div className={styles.emptyTrackPlaceholder}>
            <span>Not scheduled</span>
          </div>
        ) : (
          stages.map((stage) => {
          const isDragging =
            !!drag &&
            drag.selectedCreatorIds.includes(creatorId) &&
            drag.stageId === stage.id;
          const canDrag = editMode && rowInSelection;

          const barClass = BAR_COLOR_CLASSES[stage.kind] || styles.stageUpcoming;
          const labelClass =
            stage.kind === "upcoming" || stage.kind === "cancelled"
              ? (LABEL_COLOR_CLASSES[stage.kind] || styles.labelUpcoming)
              : styles.labelWhite;

          const stageElement = (
            <div
              key={stage.id}
              className={`
                ${styles.stageItem}
                ${barClass}
                ${canDrag ? styles.stageDraggable : ""}
                ${editMode && !canDrag ? styles.stageDisabled : ""}
                ${isDragging ? styles.stageDragging : ""}
                ${editMode && rowInSelection && !drag ? styles.stageSelectedInEdit : ""}
              `}
              style={{
                left: `${stage.leftPct}%`,
                width: `${Math.max(stage.widthPct, 2.5)}%`,
              }}
              onPointerDown={(e) => {
                if (canDrag) {
                  beginDrag(e, "move", rowContext, stage.id);
                }
              }}
              onClick={() => {
                if (!editMode && onOpenDeliverable) {
                  onOpenDeliverable(String(creatorId), deliverableId, stage.taskId);
                }
              }}
            >
              {canDrag && (
                <>
                  <span
                    className={styles.resizeHandleLeft}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      beginDrag(e, "resize-start", rowContext, stage.id);
                    }}
                  />
                  <span
                    className={styles.resizeHandleRight}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      beginDrag(e, "resize-end", rowContext, stage.id);
                    }}
                  />
                </>
              )}
              <span className={`${styles.stageLabel} ${labelClass}`}>
                {stage.label}
              </span>
            </div>
          );

          const statusLabel =
            stage.kind === "blocked"
              ? "Needs changes"
              : stage.kind === "cancelled"
              ? "Cancelled"
              : stage.kind === "done"
              ? "Done"
              : stage.kind === "upcoming"
              ? "Upcoming"
              : "In progress";

          return (
            <Tooltip
              key={stage.id}
              title={`${stage.label} (${statusLabel}): ${stage.startDate || "Not set"} → ${stage.endDate || "Not set"}`}
              mouseEnterDelay={0.3}
            >
              {stageElement}
            </Tooltip>
          );
        }))}
      </div>
    </div>
  );
};
